import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LockSimple } from 'phosphor-react';
import { useTranslation } from 'react-i18next';
import { useMemberAccess } from '@/hooks/useMemberAccess';
import { MemberStatus } from '@/types/member.types';
import SLoader from '@/components/ui/atoms/SLoader';
import { PastDueGate } from '@/components/ui/organisms/PastDueGate';
import { UiBlock, V2PastDueGate } from '@/v2/routes';

interface MemberAccessGuardProps {
  children: React.ReactNode;
}

const STATUS_LABELS: Partial<Record<MemberStatus, string>> = {
  [MemberStatus.PAUSED]: 'paused',
  [MemberStatus.PAYMENT_FAILED]: 'suspended due to a payment issue',
  [MemberStatus.SUSPENDED]: 'suspended',
};

export const MemberAccessGuard: React.FC<MemberAccessGuardProps> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAccessBlocked, isPastDue, status, isChecking, paymentFailure } = useMemberAccess();

  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-[60dvh]">
        <SLoader />
      </div>
    );
  }

  if (!isAccessBlocked) return <>{children}</>;

  // past_due is self-serve — show the payment-method update prompt instead of
  // the generic "contact your Box Pro" wall. The v2 tree has its own gate;
  // UiBlock picks the one that matches the session.
  if (isPastDue) {
    return (
      <UiBlock
        v1={<PastDueGate />}
        v2={<V2PastDueGate />}
        fallback={
          <div style={{ minHeight: '100dvh', background: 'var(--tb-bg, #FFFFFF)' }} />
        }
      />
    );
  }

  const isCancelled = status === MemberStatus.CANCELLED;
  const reason = status ? STATUS_LABELS[status] : 'restricted';

  let blockedMessage: string;
  if (paymentFailure.phase === 'ended') {
    blockedMessage = t('membership.accessBlocked.ended');
  } else if (paymentFailure.phase === 'suspended') {
    blockedMessage = t('membership.accessBlocked.suspended');
  } else if (isCancelled) {
    blockedMessage = t('membership.banner.cancelled');
  } else {
    blockedMessage = t('membership.accessBlocked.message', { reason });
  }

  return (
    <div className="flex flex-col items-center justify-center h-[60dvh] gap-6 px-8 text-center">
      <div
        className="flex items-center justify-center w-16 h-16 rounded-full"
        style={{ background: 'rgba(201,168,76,0.12)' }}
      >
        <LockSimple size={32} weight="duotone" style={{ color: 'var(--color-text-gold)' }} />
      </div>
      <div className="flex flex-col gap-2">
        <h2
          className="text-[18px] font-semibold"
          style={{ color: 'var(--text-content-title)' }}
        >
          {t('membership.accessBlocked.title')}
        </h2>
        <p
          className="text-[14px] leading-relaxed"
          style={{ color: 'var(--text-content-sub-title)' }}
        >
          {blockedMessage}
        </p>
      </div>
      <button
        onClick={() => navigate('/home')}
        className="px-6 py-3 rounded-[8px] text-[14px] font-semibold transition-opacity hover:opacity-80"
        style={{ background: 'var(--color-text-gold)', color: '#000' }}
      >
        {t('membership.accessBlocked.cta')}
      </button>
    </div>
  );
};
